import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid } from '@/components/KstDataGrid';
import { navigateToCallCard, navigateRaw } from '@/services/navigation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { getOfficerLabel, hasStringValue, hasValue } from '@/utils/legacy-utils';

import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRowAction,
  KstDataGridRows,
} from '@/components/KstDataGrid';

// ============================================================================
// External Declarations
// ============================================================================

import { getOfficerPluralLabel, getCurrentCompanyId } from '@/utils/auth';
import { hasNumericValue } from '@/utils/validation';
import { getExistingCadCallCardUrl } from '@/services/navigation';
import { displayNocValuesForGridTemplate, getGridBackupUnitClassNameFromOfficerStatus, getCADInProgressPendingClassNameFromCallDateTime } from '@/utils/call-card-helpers';
import { canCurrentUserAccessAnyClosedCalls } from '@/utils/permissions-helpers';
import { openPageInNewWindow } from '@/utils/dom';
import { isIOSApp, isAndroidApp, isWindowsAppSdkApp } from '@/services/api-class';
import { noteListReload } from '@/services/reload-burst-telemetry';

function getNewWindowIconHtmlForSearch() {
  return '<svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512" fill="#158cba"><path d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32h82.7L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3V192c0 17.7 14.3 32 32 32s32-14.3 32-32V32c0-17.7-14.3-32-32-32H320zM80 32C35.8 32 0 67.8 0 112V432c0 44.2 35.8 80 80 80H400c44.2 0 80-35.8 80-80V320c0-17.7-14.3-32-32-32s-32 14.3-32 32V432c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16H192c17.7 0 32-14.3 32-32s-14.3-32-32-32H80z"/></svg>';
}

// ============================================================================
// Module-level State (replaces InProgress static class)
// ============================================================================

let isViewingScreen = false;
let isViewingFullScreen = false;

// Module-level refresh callback for real-time updates.
// Accepts the changed callCardId from the PubSub event so the grid merges can
// distinguish the truly-changed row from pagination shifts.
let refreshCallback: ((changedCallCardId?: any) => void) | null = null;

// How long a row that actually exited a grid stays visible (strikethrough) before being removed.
// Applied only to rows whose id matches the PubSub event's callCardId.
const SOFT_REMOVE_MS = 4000;

// How long a row that disappeared due to a pagination shift stays rendered before being removed.
// Shorter than SOFT_REMOVE_MS — just long enough to preserve click safety across the brief race window.
const SHIFT_HOLD_MS = 500;

// How long an added/updated row keeps its highlight class before the flag is cleared.
// Matches the CSS animation duration so React state stays in sync with the visual.
const FLASH_MS = 1200;

// Strip internal UI flags so two rows can be compared purely on their server data.
function stripFlags(row: any): any {
  const { _transitioning, _shifting, _flash, ...rest } = row;
  return rest;
}

function idsMatch(a: any, b: any): boolean {
  return a != null && b != null && a.toString() === b.toString();
}

// Cancels any existing flash timer for this id and schedules a new one that strips
// `_flash` from the row in state once the animation has visually completed.
function scheduleFlashClear(
  id: any,
  setter: React.Dispatch<React.SetStateAction<KstDataGridRows>>,
  flashTimers: Map<any, number>,
): void {
  const existing = flashTimers.get(id);
  if (existing !== undefined) clearTimeout(existing);
  const t = window.setTimeout(() => {
    setter(curr => ({
      entries: (curr.entries || []).map(r => {
        if (r.id !== id) return r;
        const { _flash, ...rest } = r;
        return rest;
      }),
    }));
    flashTimers.delete(id);
  }, FLASH_MS);
  flashTimers.set(id, t);
}

// Merges a fresh server payload into the current grid rows. The PubSub event
// identifies which specific call changed via `changedCallCardId`, so we can
// tell a true removal/add apart from a pagination shift:
//   - The changed row, if it disappeared → `_transitioning` (strikethrough, SOFT_REMOVE_MS)
//   - The changed row, if it newly appeared → `_flash: 'added'` (FLASH_MS)
//   - The changed row, if its data changed in place → `_flash: 'updated'` (FLASH_MS)
//   - Any OTHER row that disappeared → `_shifting` (soft fade, SHIFT_HOLD_MS)
//     It didn't leave the grid, it paginated — but we still hold it briefly so
//     a click that was already in motion lands on the intended target.
//   - Any OTHER row that appeared → rendered plainly (no flash — it paginated in)
// Unchanged rows keep any in-progress `_flash` so rapid refreshes don't wipe an
// ongoing animation. Reads current rows from a ref so the outer setter stays pure.
function applyRealtimeRowUpdate(
  newRows: any[],
  currentEntries: any[],
  setter: React.Dispatch<React.SetStateAction<KstDataGridRows>>,
  transitionTimers: Map<any, number>,
  flashTimers: Map<any, number>,
  shiftTimers: Map<any, number>,
  changedCallCardId: any = null,
): void {
  const newById = new Map<any, any>();
  for (const r of newRows) newById.set(r.id, r);

  const merged: any[] = [];
  const seenIds = new Set<any>();

  for (const prev of currentEntries) {
    seenIds.add(prev.id);
    const updated = newById.get(prev.id);

    if (updated) {
      // Row still present — cancel any pending soft-remove / shift timers
      const tt = transitionTimers.get(prev.id);
      if (tt !== undefined) { clearTimeout(tt); transitionTimers.delete(prev.id); }
      const st = shiftTimers.get(prev.id);
      if (st !== undefined) { clearTimeout(st); shiftTimers.delete(prev.id); }

      const isTheChangedRow = idsMatch(prev.id, changedCallCardId);
      const changed = JSON.stringify(stripFlags(prev)) !== JSON.stringify(stripFlags(updated));

      if (changed && isTheChangedRow) {
        merged.push({ ...updated, _flash: 'updated' });
        scheduleFlashClear(updated.id, setter, flashTimers);
      } else if (prev._flash) {
        merged.push({ ...updated, _flash: prev._flash });
      } else {
        merged.push(updated);
      }
    } else if (prev._transitioning || prev._shifting) {
      // Already animating out, keep in place
      merged.push(prev);
    } else if (idsMatch(prev.id, changedCallCardId)) {
      // The known-changed row left the grid — strikethrough and slow remove
      const id = prev.id;
      merged.push({ ...prev, _transitioning: true });
      const t = window.setTimeout(() => {
        setter(curr => ({ entries: (curr.entries || []).filter(r => r.id !== id) }));
        transitionTimers.delete(id);
      }, SOFT_REMOVE_MS);
      transitionTimers.set(id, t);
    } else {
      // Row disappeared but wasn't the one that changed — pagination shift.
      // Hold briefly with a soft fade so in-flight clicks still land correctly.
      const id = prev.id;
      merged.push({ ...prev, _shifting: true });
      const t = window.setTimeout(() => {
        setter(curr => ({ entries: (curr.entries || []).filter(r => r.id !== id) }));
        shiftTimers.delete(id);
      }, SHIFT_HOLD_MS);
      shiftTimers.set(id, t);
    }
  }

  for (const r of newRows) {
    if (seenIds.has(r.id)) continue;
    if (idsMatch(r.id, changedCallCardId)) {
      merged.push({ ...r, _flash: 'added' });
      scheduleFlashClear(r.id, setter, flashTimers);
    } else {
      merged.push(r);
    }
  }

  setter({ entries: merged });
}


// ============================================================================
// Closed calls on small MDT screens
// ============================================================================

/**
 * MDT has always hidden the Closed section below 992px (phones, portrait
 * tablets). These companies asked for it on phones — supervisors use it to keep
 * track of recent calls and reopen them. For them it shows on small screens,
 * collapsed by default, and the user's expand/collapse choice is remembered on
 * the device.
 */
const MDT_SMALL_SCREEN_CLOSED_CALLS_COMPANY_IDS: ReadonlySet<number> = new Set<number>([
  2,
  1110,
]);
const MDT_SMALL_SCREEN_QUERY = '(max-width: 991px)';
const MDT_CLOSED_COLLAPSED_STORAGE_KEY = 'InProgressMdtClosedCollapsed';

function isSmallScreen(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(MDT_SMALL_SCREEN_QUERY).matches;
}

// ============================================================================
// Types
// ============================================================================

type InProgressGridType = 'pending' | 'active' | 'closed' | 'open';
type DisplayMode = 'main' | 'fullscreen-pending' | 'fullscreen-active' | 'fullscreen-closed' | 'fullscreen-panic';
type ModuleType = 'cad' | 'mdt';

interface InProgressScreenProps {
  moduleType: ModuleType;
  displayMode: DisplayMode;
}

// ============================================================================
// Template Helper Functions (module-level, return HTML strings)
// ============================================================================

function getLinkForNewWindowForInProgressScreen(relativeUrl: string): string {
  return `<a href="${relativeUrl}" target="_blank" class="nw-abs" title='View in New Window' style="margin: 2px; padding: 2px; position:relative;top:2px">${getNewWindowIconHtmlForSearch()}</a>`;
}

function getEventIdTemplate(e: any, moduleType: string, gridType: InProgressGridType): string {
  const isMdt = moduleType === 'mdt';
  const eventLinkId = isMdt ? 'lnkEventMDT' : 'lnkEvent';

  let className = '';
  if (gridType === 'pending' && !isMdt) {
    className = getCADInProgressPendingClassNameFromCallDateTime(e.callDateTime);
  }

  const eventLink = `<a id='${eventLinkId}' class='${className}' title='${e.id}' href='#'>${e.eventId}</a>`;

  // MDT doesn't show new window icon
  if (isMdt) {
    return eventLink;
  }

  const newWindowLink = getLinkForNewWindowForInProgressScreen(getExistingCadCallCardUrl(e.id));
  return `<div class="event-cell-abs">${eventLink}${newWindowLink}</div>`;
}

function getPrimaryUnitTemplate(e: any, moduleType: string): string {
  if (!e.primaryOfficerName) {
    return 'N/A';
  }

  const className = getGridBackupUnitClassNameFromOfficerStatus(e.primaryStatus);
  const isMdt = moduleType === 'mdt';

  let primaryHtml: string;
  if (isMdt) {
    primaryHtml = `<div style='width:94px; padding-left:5px;min-height:24px; padding-right:5px; align-content: center; vertical-align: middle; white-space: normal; display: grid;' class='${className}'>${e.primaryOfficerName}</div>`;
  } else {
    primaryHtml = `<div class='${className}'>${e.primaryOfficerName}</div>`;
    if (hasStringValue(e.primaryOfficerSecondaryLocation)) {
      primaryHtml += ` - ${e.primaryOfficerSecondaryLocation}`;
    }
  }

  return primaryHtml;
}

function getBackupUnitsTemplate(e: any, moduleType: string): string {
  if (!e.backupUnits || e.backupUnits.length === 0) {
    return 'N/A';
  }

  const isMdt = moduleType === 'mdt';
  let html = '';

  for (let i = 0; i < e.backupUnits.length; i++) {
    const backupUnit = e.backupUnits[i];
    const className = getGridBackupUnitClassNameFromOfficerStatus(backupUnit.status);

    if (isMdt) {
      html += `<div style='width:94px; margin:3px; padding-left:5px;min-height:24px; padding-right:5px; align-content: center; vertical-align: middle; white-space: normal; display: inline-grid;' class='${className}'>${backupUnit.name}</div>`;
    } else {
      let backupHtml = `<div class='${className}'>${backupUnit.name}</div>`;
      if (hasStringValue(backupUnit.secondaryLocation)) {
        backupHtml += ` - ${backupUnit.secondaryLocation}`;
      }
      html += backupHtml;
      if (i < e.backupUnits.length - 1) {
        html += '<br />';
      }
    }
  }

  return html;
}

function getCaseReportTemplate(e: any): string {
  if (!hasValue(e.latestCaseReportCaseId) || e.latestCaseReportCaseId === 'null') {
    return '';
  }

  // Only show link if case report is for current company
  if (getCurrentCompanyId() === e.latestCaseReportCompanyId) {
    return `<a id='lnkCaseId' data-case-report-id='${e.latestCaseReportId}' href='#'>${e.latestCaseReportCaseId}</a>`;
  }

  return e.latestCaseReportCaseId;
}

// ============================================================================
// Grid Column Definitions
// ============================================================================

function locationStatusLine(status: string | null | undefined): string {
  const line = (cls: string, text: string) => `<div class="${cls}" style="margin-top:2px;font-size:11px;">${text}</div>`;
  switch (status) {
    case 'LOW': return line('onscene-status--approx', '&asymp; Approximate location');
    case 'AREA': return line('onscene-status--area', 'Address not found');
    case 'OFFICER_ON_SCENE': return line('onscene-status--confirmed', '&#10003; Confirmed on scene');
    default: return '';
  }
}

function getInProgressGridColumns(moduleType: string, gridType: InProgressGridType): KstDataGridColumn[] {
  const isMdt = moduleType === 'mdt';
  const mapLinkId = isMdt ? 'lnkMapEventMDT' : 'lnkMapEvent';

  // Event ID column - differs for pending (color-coded) vs active/closed
  const eventIdColumn: KstDataGridColumn = {
    field: 'eventId',
    name: 'Event ID',
    size: gridType === 'pending' ? '115px' : '90px',
    template: (e: any) => getEventIdTemplate(e, moduleType, gridType),
  };

  const columns: KstDataGridColumn[] = [
    eventIdColumn,
    { field: 'priority', name: 'Priority', size: '50px' },
    {
      field: 'callDateTime',
      name: 'Date/Time',
      size: '85px',
      template: (e: any) => e.callDateTime ? moment(e.callDateTime).format('DD MMM YYYY HH:mm') : '',
    },
    {
      field: 'natureOfCallValues',
      name: 'Nature of Call',
      size: '150px',
      template: (e: any) => displayNocValuesForGridTemplate(e),
    },
    {
      field: 'npsdClientName',
      name: 'Client',
      size: '150px',
      template: (e: any) => hasStringValue(e.npsdClientName) ? e.npsdClientName : '',
    },
    {
      field: 'callLoc',
      name: 'Event Location',
      size: '175px',
      // An uncertain pin says so wherever a dispatch decision is made; verified rows are unchanged.
      template: (e: any) => `<a id='${mapLinkId}' title='${e.id}' href='#'>${e.callLoc}</a>`
        + locationStatusLine(e.addressVerificationStatus),
    },
    {
      field: 'primaryOfficerName',
      name: 'Primary Unit',
      size: '120px',
      template: (e: any) => getPrimaryUnitTemplate(e, moduleType),
    },
    {
      field: 'backupUnits',
      name: 'Back Up Units',
      size: '150px',
      template: (e: any) => getBackupUnitsTemplate(e, moduleType),
    },
  ];

  // CAD gets queued units column
  if (!isMdt) {
    columns.push({
      field: 'queuedUnitExists',
      name: '',
      size: '30px',
      template: (e: any) => e.queuedUnitExists
        ? `<img src="/Content/Images/queuedUnits.png" alt='${getOfficerPluralLabel()} Queued' title='${getOfficerPluralLabel()} Queued' />`
        : '',
    });
  }

  // Case report column
  columns.push({
    field: 'latestCaseReportCaseId',
    name: 'Case Report',
    size: '90px',
    template: (e: any) => getCaseReportTemplate(e),
  });

  return columns;
}

// ============================================================================
// Row Actions
// ============================================================================

function getInProgressViewRowActions(): KstDataGridRowAction[] {
  return [
    { name: 'View', icon: 'fa-eye', event: 'view', class: 'btn-primary', mobileOnly: true },
  ];
}

// ============================================================================
// InProgressScreen Component
// ============================================================================

export function InProgressScreen({ moduleType, displayMode }: InProgressScreenProps) {
  const { theme } = useTheme();

  // --- Pending Grid State ---
  const [isPendingLoading, setIsPendingLoading] = useState(false);
  const [pendingGridRows, setPendingGridRows] = useState<KstDataGridRows>({ entries: null });
  const [pendingPager, setPendingPager] = useState<KstDataGridPager>({ curPage: 1, numPages: 1, records: 0, pageSize: 100, isVisible: false });
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [pendingTotalCount, setPendingTotalCount] = useState(0);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const pendingPageSize = 100;

  // --- Active Grid State ---
  const [isActiveLoading, setIsActiveLoading] = useState(false);
  const [activeGridRows, setActiveGridRows] = useState<KstDataGridRows>({ entries: null });
  const [activePager, setActivePager] = useState<KstDataGridPager>({ curPage: 1, numPages: 1, records: 0, pageSize: 100, isVisible: false });
  const [activeCurrentPage, setActiveCurrentPage] = useState(1);
  const [activeTotalCount, setActiveTotalCount] = useState(0);
  const [activeTotalPages, setActiveTotalPages] = useState(1);
  const activePageSize = 100;

  // --- Closed Grid State ---
  const [isClosedLoading, setIsClosedLoading] = useState(false);
  const [closedGridRows, setClosedGridRows] = useState<KstDataGridRows>({ entries: null });
  const [closedPager, setClosedPager] = useState<KstDataGridPager>({ curPage: 1, numPages: 1, records: 0, pageSize: 100, isVisible: false });
  const [closedCurrentPage, setClosedCurrentPage] = useState(1);
  const [closedTotalCount, setClosedTotalCount] = useState(0);
  const [closedTotalPages, setClosedTotalPages] = useState(1);
  const closedPageSize = 100;

  // --- Open Grid State (MDT only) ---
  const [isOpenLoading, setIsOpenLoading] = useState(false);
  const [openGridRows, setOpenGridRows] = useState<KstDataGridRows>({ entries: null });
  const openCurrentPage = 1;
  const openPageSize = 20;

  // Guidance for an empty Unassigned grid — accurate for both brand-new
  // companies and quiet moments; the CTA only exists in CAD (MDT uses Self Initiate).
  const pendingEmptyState = useMemo(
    () => (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>No calls waiting</div>
        <div style={{ fontSize: 12.5, opacity: 0.8, marginBottom: moduleType === 'cad' ? 10 : 0 }}>
          Create a call card to dispatch your {getOfficerPluralLabel().toLowerCase()}. It takes about a minute.
        </div>
        {moduleType === 'cad' && (
          <button
            type="button"
            className="btn-action"
            onClick={() => {
              const w = window as any;
              w.OrnaReact?.navigate?.to?.({ section: 'CAD', tab: 'CallCard', fn: 'CAD_CallCard', multiModule: false });
            }}
          >
            + New call card
          </button>
        )}
      </div>
    ),
    [moduleType]
  );

  // --- Section Collapse State ---
  const [isOpenCollapsed, setIsOpenCollapsed] = useState(false);
  const [isPendingCollapsed, setIsPendingCollapsed] = useState(false);
  const [isActiveCollapsed, setIsActiveCollapsed] = useState(false);
  const [showClosedOnSmallMdtScreens] = useState(() =>
    moduleType === 'mdt' && MDT_SMALL_SCREEN_CLOSED_CALLS_COMPANY_IDS.has(getCurrentCompanyId()));
  const [isClosedCollapsed, setIsClosedCollapsed] = useState(() => {
    if (!showClosedOnSmallMdtScreens || !isSmallScreen()) return false;
    try {
      const saved = localStorage.getItem(MDT_CLOSED_COLLAPSED_STORAGE_KEY);
      if (saved !== null) return saved === 'true';
    } catch { /* storage unavailable — fall back to collapsed */ }
    return true;
  });

  // --- Permission ---
  const [canAccessAnyClosedCalls] = useState(() => canCurrentUserAccessAnyClosedCalls());

  // --- Refs ---
  const pendingCurrentPageRef = useRef(pendingCurrentPage);
  pendingCurrentPageRef.current = pendingCurrentPage;

  const activeCurrentPageRef = useRef(activeCurrentPage);
  activeCurrentPageRef.current = activeCurrentPage;

  const closedCurrentPageRef = useRef(closedCurrentPage);
  closedCurrentPageRef.current = closedCurrentPage;

  // Refs mirroring current grid rows so realtime merges can read state synchronously
  const pendingGridRowsRef = useRef(pendingGridRows);
  pendingGridRowsRef.current = pendingGridRows;
  const activeGridRowsRef = useRef(activeGridRows);
  activeGridRowsRef.current = activeGridRows;
  const closedGridRowsRef = useRef(closedGridRows);
  closedGridRowsRef.current = closedGridRows;
  const openGridRowsRef = useRef(openGridRows);
  openGridRowsRef.current = openGridRows;

  // Pending soft-remove timers per grid (id -> setTimeout handle)
  const pendingTransitionTimersRef = useRef<Map<any, number>>(new Map());
  const activeTransitionTimersRef = useRef<Map<any, number>>(new Map());
  const closedTransitionTimersRef = useRef<Map<any, number>>(new Map());
  const openTransitionTimersRef = useRef<Map<any, number>>(new Map());

  // Added/updated flash-clear timers per grid (id -> setTimeout handle)
  const pendingFlashTimersRef = useRef<Map<any, number>>(new Map());
  const activeFlashTimersRef = useRef<Map<any, number>>(new Map());
  const closedFlashTimersRef = useRef<Map<any, number>>(new Map());
  const openFlashTimersRef = useRef<Map<any, number>>(new Map());

  // Pagination-shift hold timers per grid (id -> setTimeout handle)
  const pendingShiftTimersRef = useRef<Map<any, number>>(new Map());
  const activeShiftTimersRef = useRef<Map<any, number>>(new Map());
  const closedShiftTimersRef = useRef<Map<any, number>>(new Map());
  const openShiftTimersRef = useRef<Map<any, number>>(new Map());

  // Flags for whether a realtime refresh is currently in flight per grid.
  // Drives the small spinner shown in each section header during a PubSub-triggered refresh.
  const [isPendingRefreshing, setIsPendingRefreshing] = useState(false);
  const [isActiveRefreshing, setIsActiveRefreshing] = useState(false);
  const [isClosedRefreshing, setIsClosedRefreshing] = useState(false);
  const [isOpenRefreshing, setIsOpenRefreshing] = useState(false);

  // Clear all pending timers in a map — used when switching pages or unmounting
  const clearTransitionTimers = useCallback((timers: Map<any, number>) => {
    for (const t of timers.values()) clearTimeout(t);
    timers.clear();
  }, []);

  // --- Column Definitions (memoized) ---
  const pendingGridColumns = useMemo(() => getInProgressGridColumns(moduleType, 'pending'), [moduleType]);
  const activeGridColumns = useMemo(() => getInProgressGridColumns(moduleType, 'active'), [moduleType]);
  const closedGridColumns = useMemo(() => getInProgressGridColumns(moduleType, 'closed'), [moduleType]);
  const openGridColumns = useMemo(() => getInProgressGridColumns(moduleType, 'open'), [moduleType]);

  // --- Row Actions ---
  const viewRowActions = useMemo(() => getInProgressViewRowActions(), []);

  // --- Computed Visibility ---
  const isMainView = useMemo(() => displayMode === 'main', [displayMode]);

  const showOpenGrid = useMemo(
    () => isMainView && moduleType === 'mdt',
    [isMainView, moduleType]
  );

  const showPendingGrid = useMemo(
    () => isMainView || displayMode === 'fullscreen-pending',
    [isMainView, displayMode]
  );

  const showActiveGrid = useMemo(
    () => isMainView || displayMode === 'fullscreen-active',
    [isMainView, displayMode]
  );

  const showClosedGrid = useMemo(
    () => (isMainView || displayMode === 'fullscreen-closed') && canAccessAnyClosedCalls,
    [isMainView, displayMode, canAccessAnyClosedCalls]
  );

  const showPanicGrid = useMemo(
    () => displayMode === 'fullscreen-panic',
    [displayMode]
  );

  // ===== Load Functions =====

  const loadOpenCards = useCallback((isRealtime: boolean = false, changedCallCardId: any = null) => {
    if (moduleType !== 'mdt') return;

    if (!isRealtime) {
      setIsOpenLoading(true);
      clearTransitionTimers(openTransitionTimersRef.current);
      clearTransitionTimers(openFlashTimersRef.current);
      clearTransitionTimers(openShiftTimersRef.current);
    } else {
      setIsOpenRefreshing(true);
    }
    api.getMDTList(moduleType, "open", openCurrentPage, openPageSize, (result: any) => {
      const rows = result.rows || [];
      if (isRealtime) {
        applyRealtimeRowUpdate(
          rows,
          openGridRowsRef.current.entries || [],
          setOpenGridRows,
          openTransitionTimersRef.current,
          openFlashTimersRef.current,
          openShiftTimersRef.current,
          changedCallCardId,
        );
        setIsOpenRefreshing(false);
      } else {
        setOpenGridRows({ entries: rows });
        setIsOpenLoading(false);
      }
    }, isRealtime);
  }, [moduleType, clearTransitionTimers]);

  const loadPendingCards = useCallback((pageOverride?: number, isRealtime: boolean = false, changedCallCardId: any = null) => {
    if (!isRealtime) {
      setIsPendingLoading(true);
      clearTransitionTimers(pendingTransitionTimersRef.current);
      clearTransitionTimers(pendingFlashTimersRef.current);
      clearTransitionTimers(pendingShiftTimersRef.current);
    } else {
      setIsPendingRefreshing(true);
    }
    const currentPage = pageOverride !== undefined ? pageOverride : pendingCurrentPageRef.current;

    const callback = (result: any) => {
      const totalCount = result.records || 0;
      const totalPages = Math.ceil(totalCount / pendingPageSize) || 1;
      const rows = result.rows || [];

      setPendingTotalCount(totalCount);
      setPendingTotalPages(totalPages);
      if (isRealtime) {
        applyRealtimeRowUpdate(
          rows,
          pendingGridRowsRef.current.entries || [],
          setPendingGridRows,
          pendingTransitionTimersRef.current,
          pendingFlashTimersRef.current,
          pendingShiftTimersRef.current,
          changedCallCardId,
        );
        setIsPendingRefreshing(false);
      } else {
        setPendingGridRows({ entries: rows });
      }
      setPendingPager({
        curPage: currentPage,
        numPages: totalPages,
        records: totalCount,
        pageSize: pendingPageSize,
        isVisible: totalPages > 1,
      });
      if (!isRealtime) setIsPendingLoading(false);
    };

    if (moduleType === 'mdt') {
      api.getMDTList(moduleType, "pending", currentPage, pendingPageSize, callback, isRealtime);
    } else {
      api.getCallCards(moduleType, "pending", currentPage, pendingPageSize, callback, isRealtime);
    }
  }, [moduleType, clearTransitionTimers]);

  const loadActiveCards = useCallback((pageOverride?: number, isRealtime: boolean = false, changedCallCardId: any = null) => {
    if (!isRealtime) {
      setIsActiveLoading(true);
      clearTransitionTimers(activeTransitionTimersRef.current);
      clearTransitionTimers(activeFlashTimersRef.current);
      clearTransitionTimers(activeShiftTimersRef.current);
    } else {
      setIsActiveRefreshing(true);
    }
    const currentPage = pageOverride !== undefined ? pageOverride : activeCurrentPageRef.current;

    const callback = (result: any) => {
      const totalCount = result.records || 0;
      const totalPages = Math.ceil(totalCount / activePageSize) || 1;
      const rows = result.rows || [];

      setActiveTotalCount(totalCount);
      setActiveTotalPages(totalPages);
      if (isRealtime) {
        applyRealtimeRowUpdate(
          rows,
          activeGridRowsRef.current.entries || [],
          setActiveGridRows,
          activeTransitionTimersRef.current,
          activeFlashTimersRef.current,
          activeShiftTimersRef.current,
          changedCallCardId,
        );
        setIsActiveRefreshing(false);
      } else {
        setActiveGridRows({ entries: rows });
      }
      setActivePager({
        curPage: currentPage,
        numPages: totalPages,
        records: totalCount,
        pageSize: activePageSize,
        isVisible: totalPages > 1,
      });
      if (!isRealtime) setIsActiveLoading(false);
    };

    if (moduleType === 'mdt') {
      api.getMDTList(moduleType, "active", currentPage, activePageSize, callback, isRealtime);
    } else {
      api.getCallCards(moduleType, "active", currentPage, activePageSize, callback, isRealtime);
    }
  }, [moduleType, clearTransitionTimers]);

  const loadClosedCards = useCallback((pageOverride?: number, isRealtime: boolean = false, changedCallCardId: any = null) => {
    if (!isRealtime) {
      setIsClosedLoading(true);
      clearTransitionTimers(closedTransitionTimersRef.current);
      clearTransitionTimers(closedFlashTimersRef.current);
      clearTransitionTimers(closedShiftTimersRef.current);
    } else {
      setIsClosedRefreshing(true);
    }
    const currentPage = pageOverride !== undefined ? pageOverride : closedCurrentPageRef.current;

    const callback = (result: any) => {
      const totalCount = result.records || 0;
      const totalPages = Math.ceil(totalCount / closedPageSize) || 1;
      const rows = result.rows || [];

      setClosedTotalCount(totalCount);
      setClosedTotalPages(totalPages);
      if (isRealtime) {
        applyRealtimeRowUpdate(
          rows,
          closedGridRowsRef.current.entries || [],
          setClosedGridRows,
          closedTransitionTimersRef.current,
          closedFlashTimersRef.current,
          closedShiftTimersRef.current,
          changedCallCardId,
        );
        setIsClosedRefreshing(false);
      } else {
        setClosedGridRows({ entries: rows });
      }
      setClosedPager({
        curPage: currentPage,
        numPages: totalPages,
        records: totalCount,
        pageSize: closedPageSize,
        isVisible: totalPages > 1,
      });
      if (!isRealtime) setIsClosedLoading(false);
    };

    if (moduleType === 'mdt') {
      api.getMDTList(moduleType, "closed", currentPage, closedPageSize, callback, isRealtime);
    } else {
      api.getCallCards(moduleType, "closed", currentPage, closedPageSize, callback, isRealtime);
    }
  }, [moduleType, clearTransitionTimers]);

  const loadPanicCards = useCallback(() => {
    setIsActiveLoading(true);

    api.getPanicCallCards(1, '10000', (result: any) => {
      // If exactly one panic call, navigate directly to it
      if (hasValue(result) && hasValue(result.rows) && result.rows.length === 1) {
        navigateToCallCard(result.rows[0].id, 'CAD');
        App.showPopupNotification('Forwarded to the panic call.', 'success');
        return;
      }

      // Otherwise display the grid
      const rows = result.rows || [];
      setActiveGridRows({ entries: rows });
      setIsActiveLoading(false);
    }, () => {
      App.showPopupNotification('Error retrieving panic calls.', 'error');
      setIsActiveLoading(false);
    });
  }, []);

  // ===== Load All Grids =====

  const loadAllGrids = useCallback((isRealtime: boolean = false, changedCallCardId: any = null) => {
    noteListReload(String(moduleType), isRealtime);
    if (moduleType === 'mdt') {
      loadOpenCards(isRealtime, changedCallCardId);
    }
    loadPendingCards(undefined, isRealtime, changedCallCardId);
    loadActiveCards(undefined, isRealtime, changedCallCardId);
    if (canAccessAnyClosedCalls) {
      loadClosedCards(undefined, isRealtime, changedCallCardId);
    }
  }, [moduleType, canAccessAnyClosedCalls, loadOpenCards, loadPendingCards, loadActiveCards, loadClosedCards]);

  // ===== Register refresh callback for real-time updates =====

  useEffect(() => {
    const isFullScreen = displayMode !== 'main';
    if (isFullScreen) {
      isViewingFullScreen = true;
    } else {
      isViewingScreen = true;
    }
    refreshCallback = (changedCallCardId?: any) => loadAllGrids(true, changedCallCardId);
    return () => {
      refreshCallback = null;
      isViewingScreen = false;
      isViewingFullScreen = false;
    };
  }, [loadAllGrids, displayMode]);

  // Clear any pending soft-remove, shift, and flash timers on unmount
  useEffect(() => {
    return () => {
      clearTransitionTimers(pendingTransitionTimersRef.current);
      clearTransitionTimers(activeTransitionTimersRef.current);
      clearTransitionTimers(closedTransitionTimersRef.current);
      clearTransitionTimers(openTransitionTimersRef.current);
      clearTransitionTimers(pendingFlashTimersRef.current);
      clearTransitionTimers(activeFlashTimersRef.current);
      clearTransitionTimers(closedFlashTimersRef.current);
      clearTransitionTimers(openFlashTimersRef.current);
      clearTransitionTimers(pendingShiftTimersRef.current);
      clearTransitionTimers(activeShiftTimersRef.current);
      clearTransitionTimers(closedShiftTimersRef.current);
      clearTransitionTimers(openShiftTimersRef.current);
    };
  }, [clearTransitionTimers]);

  // ===== Initial Data Load =====

  useEffect(() => {
    if (displayMode === 'main') {
      loadAllGrids();
    } else if (displayMode === 'fullscreen-active') {
      loadActiveCards();
    } else if (displayMode === 'fullscreen-pending') {
      loadPendingCards();
    } else if (displayMode === 'fullscreen-closed') {
      loadClosedCards();
    } else if (displayMode === 'fullscreen-panic') {
      loadPanicCards();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Pagination Handlers =====

  const pendingGoToPage = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > pendingTotalPages) return;
    setPendingCurrentPage(newPage);
    loadPendingCards(newPage);
  }, [pendingTotalPages, loadPendingCards]);

  const activeGoToPage = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > activeTotalPages) return;
    setActiveCurrentPage(newPage);
    loadActiveCards(newPage);
  }, [activeTotalPages, loadActiveCards]);

  const closedGoToPage = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > closedTotalPages) return;
    setClosedCurrentPage(newPage);
    loadClosedCards(newPage);
  }, [closedTotalPages, loadClosedCards]);

  // ===== Full Screen Navigation (CAD only) =====

  const showFullScreenCards = useCallback((cardType: string) => {
    const relativeUrl = `/?sectionName=CAD&tab=InProgress${cardType}CardsFullScreen&function=CAD_InProgress${cardType}CardsFullScreen&multiModule=true`;

    if (isIOSApp || isAndroidApp || isWindowsAppSdkApp) {
      navigateRaw(relativeUrl);
    } else {
      openPageInNewWindow(relativeUrl);
    }
  }, []);

  const showFullScreenPendingCards = useCallback(() => {
    showFullScreenCards('Pending');
  }, [showFullScreenCards]);

  const showFullScreenActiveCards = useCallback(() => {
    showFullScreenCards('Active');
  }, [showFullScreenCards]);

  const showFullScreenClosedCards = useCallback(() => {
    showFullScreenCards('Closed');
  }, [showFullScreenCards]);

  // ===== Section Collapse Toggles =====

  const scrollHeaderIntoView = useCallback((event: React.MouseEvent) => {
    const header = event.currentTarget as HTMLElement;
    if (header) {
      setTimeout(() => {
        header.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    }
  }, []);

  const toggleOpenCollapsed = useCallback((event: React.MouseEvent) => {
    setIsOpenCollapsed(prev => !prev);
    scrollHeaderIntoView(event);
  }, [scrollHeaderIntoView]);

  const togglePendingCollapsed = useCallback((event: React.MouseEvent) => {
    setIsPendingCollapsed(prev => !prev);
    scrollHeaderIntoView(event);
  }, [scrollHeaderIntoView]);

  const toggleActiveCollapsed = useCallback((event: React.MouseEvent) => {
    setIsActiveCollapsed(prev => !prev);
    scrollHeaderIntoView(event);
  }, [scrollHeaderIntoView]);

  const toggleClosedCollapsed = useCallback((event: React.MouseEvent) => {
    setIsClosedCollapsed(prev => {
      const next = !prev;
      if (showClosedOnSmallMdtScreens && isSmallScreen()) {
        try {
          localStorage.setItem(MDT_CLOSED_COLLAPSED_STORAGE_KEY, String(next));
        } catch { /* best-effort */ }
      }
      return next;
    });
    scrollHeaderIntoView(event);
  }, [scrollHeaderIntoView, showClosedOnSmallMdtScreens]);

  // ===== Row Action (Mobile View Button) =====

  const onRowAction = useCallback((event: { action: string; row: any }) => {
    if (event.action === 'view' && event.row.id) {
      navigateToCallCard(event.row.id, moduleType === 'mdt' ? 'MDT' : 'CAD');
    }
  }, [moduleType]);

  // ===== Render: Fullscreen Modes =====

  if (displayMode === 'fullscreen-pending') {
    return (
      <div className={`in-progress-fullscreen ${theme}`.trim()}>
        <div className="in-progress-header">
          <h3>Unassigned</h3>
          <div className="in-progress-header-actions">
            {isPendingRefreshing && (
              <div className="loading-spinner in-progress-refresh-spinner" role="status" aria-label="Refreshing"></div>
            )}
            <div className="in-progress-legend hide-mobile">
              <span className="call-label call-label-StatusGreen">&lt; 5min</span>
              <span className="call-label call-label-StatusYellow">&lt; 15min</span>
              <span className="call-label call-label-StatusOrange">&lt; 30min</span>
              <span className="call-label call-label-StatusRed">&lt; 45 min</span>
              <span className="call-label call-label-StatusRed blink_me">&gt; 45 min</span>
            </div>
          </div>
        </div>
        <div className="in-progress-grid-wrapper">
          <KstDataGrid
            columns={pendingGridColumns}
            rows={pendingGridRows}
            pager={pendingPager}
            loading={isPendingLoading}
            showheader={false}
            showcheckbox={false}
            mobiletitlefield="eventId"
            rowactions={viewRowActions}
            mobileactionsonly={true}
            onGoToPage={pendingGoToPage}
            onRowAction={onRowAction}
          />
        </div>
      </div>
    );
  }

  if (displayMode === 'fullscreen-active') {
    return (
      <div className={`in-progress-fullscreen ${theme}`.trim()}>
        <div className="in-progress-header">
          <h3>Assigned</h3>
          <div className="in-progress-header-actions">
            {isActiveRefreshing && (
              <div className="loading-spinner in-progress-refresh-spinner" role="status" aria-label="Refreshing"></div>
            )}
            <div className="in-progress-legend hide-mobile">
              <span className="label label-OrnaDispatched">DISPATCHED</span>
              <span className="label label-OrnaEnroute">EN-ROUTE</span>
              <span className="label label-OrnaOnScene">ON-SCENE</span>
              <span className="label label-OrnaCleared">CLEARED</span>
            </div>
          </div>
        </div>
        <div className="in-progress-grid-wrapper">
          <KstDataGrid
            columns={activeGridColumns}
            rows={activeGridRows}
            pager={activePager}
            loading={isActiveLoading}
            showheader={false}
            showcheckbox={false}
            mobiletitlefield="eventId"
            rowactions={viewRowActions}
            mobileactionsonly={true}
            onGoToPage={activeGoToPage}
            onRowAction={onRowAction}
          />
        </div>
      </div>
    );
  }

  if (displayMode === 'fullscreen-closed') {
    return (
      <div className={`in-progress-fullscreen ${theme}`.trim()}>
        <div className="in-progress-header">
          <h3>Closed Calls</h3>
          {isClosedRefreshing && (
            <div className="in-progress-header-actions">
              <div className="loading-spinner in-progress-refresh-spinner" role="status" aria-label="Refreshing"></div>
            </div>
          )}
        </div>
        <div className="in-progress-grid-wrapper">
          <KstDataGrid
            columns={closedGridColumns}
            rows={closedGridRows}
            pager={closedPager}
            loading={isClosedLoading}
            showheader={false}
            showcheckbox={false}
            mobiletitlefield="eventId"
            rowactions={viewRowActions}
            mobileactionsonly={true}
            onGoToPage={closedGoToPage}
            onRowAction={onRowAction}
          />
        </div>
      </div>
    );
  }

  if (displayMode === 'fullscreen-panic') {
    return (
      <div className={`in-progress-fullscreen ${theme}`.trim()}>
        <div className="in-progress-header">
          <h3>Panic</h3>
          <div className="in-progress-header-actions">
            <div className="in-progress-legend hide-mobile">
              <span className="label label-OrnaDispatched">DISPATCHED</span>
              <span className="label label-OrnaEnroute">EN-ROUTE</span>
              <span className="label label-OrnaOnScene">ON-SCENE</span>
              <span className="label label-OrnaCleared">CLEARED</span>
            </div>
          </div>
        </div>
        <div className="in-progress-grid-wrapper">
          <KstDataGrid
            columns={activeGridColumns}
            rows={activeGridRows}
            loading={isActiveLoading}
            showheader={false}
            showcheckbox={false}
            mobiletitlefield="eventId"
            rowactions={viewRowActions}
            mobileactionsonly={true}
            onRowAction={onRowAction}
          />
        </div>
      </div>
    );
  }

  // ===== Render: Main View =====

  return (
    <div className={`in-progress-page ${theme}`.trim()}>
      {/* Open Grid (MDT only - "My Call") */}
      {showOpenGrid && (
        <div className={`in-progress-section${isOpenCollapsed ? ' is-collapsed' : ''}`}>
          <div className="in-progress-header" onClick={toggleOpenCollapsed}>
            <h3>
              <i className={`fa section-collapse-icon ${isOpenCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}></i>
              My Call
            </h3>
            <div className="in-progress-header-actions">
              {isOpenRefreshing && (
                <div className="loading-spinner in-progress-refresh-spinner" role="status" aria-label="Refreshing"></div>
              )}
              <div className="in-progress-legend hide-mobile">
                <span className="label label-OrnaDispatched">DISPATCHED</span>
                <span className="label label-OrnaEnroute">EN-ROUTE</span>
                <span className="label label-OrnaOnScene">ON-SCENE</span>
                <span className="label label-OrnaCleared">CLEARED</span>
              </div>
            </div>
          </div>
          {!isOpenCollapsed && (
            <div className="in-progress-grid-wrapper">
              <KstDataGrid
                columns={openGridColumns}
                rows={openGridRows}
                loading={isOpenLoading}
                showheader={false}
                showcheckbox={false}
                mobiletitlefield="eventId"
                rowactions={viewRowActions}
                mobileactionsonly={true}
                onRowAction={onRowAction}
              />
            </div>
          )}
        </div>
      )}

      {/* Pending/Unassigned Grid */}
      {showPendingGrid && (
        <div className={`in-progress-section${isPendingCollapsed ? ' is-collapsed' : ''}`}>
          <div className="in-progress-header" onClick={togglePendingCollapsed}>
            <h3>
              <i className={`fa section-collapse-icon ${isPendingCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}></i>
              Unassigned
              {isPendingCollapsed && pendingTotalCount > 0 && (
                <span className="section-count">({pendingTotalCount})</span>
              )}
            </h3>
            <div className="in-progress-header-actions">
              {isPendingRefreshing && (
                <div className="loading-spinner in-progress-refresh-spinner" role="status" aria-label="Refreshing"></div>
              )}
              {moduleType === 'cad' && (
                <>
                  <div className="in-progress-legend hide-mobile">
                    <span className="call-label call-label-StatusGreen">&lt; 5min</span>
                    <span className="call-label call-label-StatusYellow">&lt; 15min</span>
                    <span className="call-label call-label-StatusOrange">&lt; 30min</span>
                    <span className="call-label call-label-StatusRed">&lt; 45 min</span>
                    <span className="call-label call-label-StatusRed blink_me">&gt; 45 min</span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); showFullScreenPendingCards(); }}
                    className="btn-fullscreen hide-mobile"
                  >
                    <i className="fa fa-expand"></i>
                  </button>
                </>
              )}
            </div>
          </div>
          {!isPendingCollapsed && (
            <div className="in-progress-grid-wrapper">
              <KstDataGrid
                columns={pendingGridColumns}
                rows={pendingGridRows}
                pager={pendingPager}
                loading={isPendingLoading}
                showheader={false}
                showcheckbox={false}
                mobiletitlefield="eventId"
                rowactions={viewRowActions}
                mobileactionsonly={true}
                emptyState={pendingEmptyState}
                onGoToPage={pendingGoToPage}
                onRowAction={onRowAction}
              />
            </div>
          )}
        </div>
      )}

      {/* Active/Assigned Grid */}
      {showActiveGrid && (
        <div className={`in-progress-section${isActiveCollapsed ? ' is-collapsed' : ''}`}>
          <div className="in-progress-header" onClick={toggleActiveCollapsed}>
            <h3>
              <i className={`fa section-collapse-icon ${isActiveCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}></i>
              Assigned
              {isActiveCollapsed && activeTotalCount > 0 && (
                <span className="section-count">({activeTotalCount})</span>
              )}
            </h3>
            <div className="in-progress-header-actions">
              {isActiveRefreshing && (
                <div className="loading-spinner in-progress-refresh-spinner" role="status" aria-label="Refreshing"></div>
              )}
              {moduleType === 'cad' && (
                <>
                  <div className="in-progress-legend hide-mobile">
                    <span className="label label-OrnaDispatched">DISPATCHED</span>
                    <span className="label label-OrnaEnroute">EN-ROUTE</span>
                    <span className="label label-OrnaOnScene">ON-SCENE</span>
                    <span className="label label-OrnaCleared">CLEARED</span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); showFullScreenActiveCards(); }}
                    className="btn-fullscreen hide-mobile"
                  >
                    <i className="fa fa-expand"></i>
                  </button>
                </>
              )}
            </div>
          </div>
          {!isActiveCollapsed && (
            <div className="in-progress-grid-wrapper">
              <KstDataGrid
                columns={activeGridColumns}
                rows={activeGridRows}
                pager={activePager}
                loading={isActiveLoading}
                showheader={false}
                showcheckbox={false}
                mobiletitlefield="eventId"
                rowactions={viewRowActions}
                mobileactionsonly={true}
                onGoToPage={activeGoToPage}
                onRowAction={onRowAction}
              />
            </div>
          )}
        </div>
      )}

      {/* Closed Grid (permission-gated) */}
      {showClosedGrid && (
        <div className={`in-progress-section in-progress-section--closed${moduleType === 'mdt' && !showClosedOnSmallMdtScreens ? ' hide-small' : ''}${isClosedCollapsed ? ' is-collapsed' : ''}`}>
          <div className="in-progress-header" onClick={toggleClosedCollapsed}>
            <h3>
              <i className={`fa section-collapse-icon ${isClosedCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}></i>
              Closed Calls
              {isClosedCollapsed && closedTotalCount > 0 && (
                <span className="section-count">({closedTotalCount})</span>
              )}
            </h3>
            <div className="in-progress-header-actions">
              {isClosedRefreshing && (
                <div className="loading-spinner in-progress-refresh-spinner" role="status" aria-label="Refreshing"></div>
              )}
              {moduleType === 'cad' && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); showFullScreenClosedCards(); }}
                  className="btn-fullscreen hide-mobile"
                >
                  <i className="fa fa-expand"></i>
                </button>
              )}
            </div>
          </div>
          {!isClosedCollapsed && (
            <div className="in-progress-grid-wrapper">
              <KstDataGrid
                columns={closedGridColumns}
                rows={closedGridRows}
                pager={closedPager}
                loading={isClosedLoading}
                showheader={false}
                showcheckbox={false}
                mobiletitlefield="eventId"
                rowactions={viewRowActions}
                mobileactionsonly={true}
                onGoToPage={closedGoToPage}
                onRowAction={onRowAction}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Imperative Functions (bridge for legacy code)
// ============================================================================

function updateInProgressScreenFromEvent(callCardId?: any): void {
  if ((isViewingScreen || isViewingFullScreen) && refreshCallback) {
    refreshCallback(callCardId);
  }
}

function cleanUpInProgressScreen(): void {
  isViewingScreen = false;
  isViewingFullScreen = false;
}

// ============================================================================
// Window Registration
// ============================================================================

(window as any).updateInProgressScreenFromEvent = updateInProgressScreenFromEvent;
(window as any).cleanUpInProgressScreen = cleanUpInProgressScreen;
// legacy aliases
(window as any).updateInProgressScreenFromEventVue = updateInProgressScreenFromEvent;
(window as any).cleanUpInProgressVue = cleanUpInProgressScreen;
(window as any).resizeInProgressFullScreenGrid = () => {}; // no-op, React handles resizing

// ============================================================================
// Exports
// ============================================================================

export {
  updateInProgressScreenFromEvent,
  cleanUpInProgressScreen,
};
